/**
 * **编辑器契约** —— 「一个 Area 里能放什么」的纯数据部分。
 *
 * ## 为什么它在 `types/` 而不是 `layout/`
 *
 * 这份契约现在有**两个**消费者，而且它们分处两层：
 *
 * ```text
 *   node/package.ts       NodePackage.editors —— 包能贡献编辑器（纯数据）
 *   layout/areaContract   区域表 / 默认 Region / 树上的 editor 字段
 * ```
 *
 * `node/` 只许依赖 `types/`（见 `verify-layers`），所以这份形状必须
 * 住在两边都够得着的层 —— 这就是它在这儿的原因，不是因为它"更基础"。
 *
 * ## ⚠️ 这里**不认识 Vue**
 *
 * 贡献里的 `component` 声明成 `unknown`：一个包能不能写 Vue 组件是
 * **宿主**的事（今天的宿主是 Vue，见 `playground/nodeTypes.ts` 的装配点）。
 * 契约只说"这里有一个东西负责画这个窗口"，收窄成 `Component` 是 Vue 宿主
 * 那一侧的动作（`Playground.vue` 里那三行 `as Component`）。
 * 这样 `types/` 与 `node/` 仍然是零框架的 —— 和 `package-boundaries.md`
 * 的 L1 是同一条线。
 *
 * ## 「有哪些编辑器」= 内核的 + 包贡献的
 *
 * ⚠️ **别把 `CoreEditorType` 当成全集**。它是**内核自带**的那几条；
 * 完整清单是 `layout/editorRegistry.ts` 里合并之后的 `EDITOR_META` /
 * `EDITOR_ORDER`。包可以注册新的编辑器类型（`EditorContribution`）。
 */

/**
 * 内核**自带**的编辑器类型。
 *
 * ⚠️ 它是**开放联合**的一半：`EditorType` 还允许任意字符串
 * （包注册的类型不在这个字面量里）。保留下面的字面量是为了
 * 内核代码里仍有自动补全和错字检查 —— 直接写 `string` 会把这层保护丢掉。
 */
export type CoreEditorType =
  | 'graph' /* 节点图（自研画布） */
  | 'outliner' /* 大纲：节点树 / 层级 / 组内成员 */
  | 'properties' /* 属性：选中节点的行与部件 */
  | 'console' /* 输出：求值问题 / 校验结果 */
  | 'library' /* 节点库：可搜索的「添加节点」 */
  | 'devices' /* 设备（预留给不在这份代码里的编辑器） */
  | 'theme' /* 主题：调色板 / 配方 / 密度 / 自定义 CSS */
  /** 文档：库里的文档列表 / 改名 / 打开 / 导入导出（2027 加，也能当首页用） */
  | 'documents'

/**
 * 一个 Area 显示哪种编辑器。
 *
 * ⚠️ `(string & {})` 是**故意的**：包注册的编辑器类型是运行时才知道的字符串。
 * 用 `string` 会连内核那几条的补全一起丢掉，所以是「字面量 ∪ 任意字符串」。
 * 代价是写错内核编辑器的名字不会报错 —— 这条由 `verify-layout` 的
 * 元数据自洽断言兜着（`EDITOR_ORDER` / 能力表都是真的查得到的那种）。
 */
export type EditorType = CoreEditorType | (string & {})

/**
 * Area 内的可开关区块。
 *
 * ⚠️ `minimap` **不是** Region（小地图是画布里的覆盖层，
 * 见 `docs/layout-redesign.md` §4.4），但它的开关状态和 Region 同源同命 ——
 * 都是「这个 Area 的一个显示项」。拆成两个字段只会让 `toggleRegion`
 * 多一层分支，不值得。所以放在一起，由 `EDITOR_META` 声明哪种编辑器支持哪几项。
 *
 * ⚠️ 这里删掉过三个键，都别加回来：
 *   · `legend`（配置行）—— 实测反馈「没啥用处了」。那些信息本来就在
 *     「属性」窗口里，挂一条常驻横条只是白占一行高度；
 *   · `toolbar`（左侧工具栏）—— 工具栏整个删掉之后它没有任何内容了，
 *     「工具栏按钮」改成了**左侧 tab 面板按钮**（见 `EditorMeta.panels`）。
 *     留一个没有内容的 Region 开关 = 一个点了没反应的按钮；
 *   · `statusbar`（每个窗口自己的提示条）—— 改成了**全局唯一**的底部条
 *     （见 `Playground.vue` 的 `.pg__statusbar`）。Blender 也是这样：
 *     提示条有一根，内容是「鼠标底下那个窗口」的。
 *     每个 Area 一条的话，分屏之后会看到两条一模一样的提示，
 *     而且它们各占一份高度 —— 窗口越分越小，提示却越铺越多。
 */
export type RegionKey = 'header' | 'minimap'

/**
 * Region 的中文名 —— **单一来源**。
 *
 * ⚠️ 抽出来是因为它有两个消费者：`NpArea` 头部的开关按钮，
 * 以及「⋯」/右键 Area 菜单（收起之后用户要靠菜单够到这些开关）。
 * 两处各写一份的话，改一个忘一个的表现是「按钮叫工具栏、菜单里叫侧栏」。
 */
export const REGION_LABELS: Record<RegionKey, string> = {
  header: '标题栏',
  minimap: '小地图',
}

export const ALL_REGIONS: readonly RegionKey[] = ['header', 'minimap']

export type RegionState = Record<RegionKey, boolean>

/**
 * 一个侧栏 tab。
 *
 * 参考 Blender 的 N / T 侧栏：窗口左右各可以挂一条**竖排 tab 条**，
 * 每个 tab 是一块浮动面板。这是**窗口的基础能力**，不是某个编辑器特有的 ——
 * 所以它声明在 `EDITOR_META` 上，由 `NpArea` 统一渲染。
 *
 * ⚠️ 编辑器**没声明就不显示开关**（`panels` 为空 = 这个窗口没有侧栏）。
 * 摆一个点了没反应的按钮比没有按钮更糟。
 */
export interface PanelTabDef {
  id: string
  label: string
  /** 24×24 viewBox 的 stroke 路径 */
  icon: string
  /**
   * tab 的内容组件。
   *
   * ⚠️ **只有包贡献的 tab 才有它**：内核那几个 tab 的内容由宿主按
   * `(editor, side, tabId)` 分派（比如节点图右侧那页「属性」就是
   * `PlaygroundProperties`，和属性窗口共用同一个组件）。
   * 包贡献的内容宿主**不认识**，所以必须由包自己给。
   */
  component?: unknown
}

export type PanelSide = 'left' | 'right'

/**
 * 底部提示条上的一条操作提示。
 *
 * `keys` 是**给用户看的写法**（`滚轮` / `双指` / `Del` / `Ctrl+Z`）——
 * 内核只负责把它排成 `<kbd>` 的样子，不解析、不改键。
 */
export interface EditorTip {
  keys: string
  label: string
  /**
   * **触摸端换一种说法**（不写 = 那条在触摸端不显示）。
   *
   * ⚠️ 为什么不是"同一句话换个词"：桌面端的"滚轮缩放 / Del 删除 / Ctrl+Z"
   * 在手机上**根本不存在**（没有滚轮、没有键盘），而手机上真实可用的是
   * "双指捏合 / 长按"。真机反馈："应该是非移动端的时候，才显示滚轮、Delete
   * 删掉还有快捷键，移动端的时候应该显示手指的操作"。
   *
   * 所以每条提示按**当前是不是触摸设备**二选一，没有对应说法的直接不显示。
   */
  touchKeys?: string
  touchLabel?: string
}

export interface EditorMeta {
  label: string
  /**
   * 窗口类型图标（24×24 viewBox 的 stroke 路径）。
   *
   * ⚠️ header 里**用图标代替标题文字**（Blender 的做法）：
   * 窗口一多，一排「节点图 / 节点图 / 输出」既占地方又难扫；
   * 图标能在一眼之内分辨，而文字留给 tooltip 和读屏。
   */
  icon: string
  /** 单选快捷键用的数字（Blender 的 Ctrl+1..9 思路） */
  digit: number
  /**
   * **这个编辑器实现了没有。**
   *
   * ⚠️ 为什么要显式写出来，而不是等它自己「有没有内容」：
   *   · Area header 的下拉里要把未实现的**置灰**（而不是让人选中之后
   *     面对一个空盒子，以为界面坏了）；
   *   · 手机档的底部模式栏**只列已实现的** —— 一排切过去没反应的标签
   *     比少几个标签糟得多。
   * 内核里已实现的那几个之外都是规划中的，见
   * `docs/layout-redesign.md` §3.2；**包贡献的一律是已实现**
   * （它必须带 `component`，见 `EditorContribution`）。
   */
  implemented: boolean
  /** 这种编辑器支持哪些 Region（没列出来的一律视为不支持、也不渲染开关） */
  regions: readonly RegionKey[]
  /**
   * 侧栏 tab。没写 = 这个窗口**没有侧栏**，header 上也不出现开关。
   * 内容由宿主按 `(editor, side, tabId)` 渲染；包贡献的 tab 自带组件。
   */
  panels?: Partial<Record<PanelSide, readonly PanelTabDef[]>>
  /**
   * **窗口头部的附加控件**（可选）。
   *
   * 和 `panels` 同一条理由：**窗口长什么样归包**。缩放档位 / 视图切换这类
   * "看哪儿"的操作属于窗口本身 —— 放在正文工具栏里，它们会和
   * "往纸上加东西"混成一片（真机反馈："页面编辑器中有很多的按钮，
   * 这一部分可以放在窗口头部"）。
   *
   * ⚠️ 宿主**不认识**这个组件画的是什么，只负责摆位置：它只把 `area`
   * 传进去，组件自己按 `area.id` 去读包的视图状态。所以这里**不许**
   * 出现"按编辑器类型写死"的分支（那是 `node-packages.md` §4 的 B 路）。
   */
  header?: unknown
  /**
   * **底部提示条上显示的快捷键 / 操作方式**（可选）。
   *
   * ⚠️ 真机反馈："页面编辑器的快捷操作方式，我没有在快捷条里边看到有任何提示渲染"。
   * 底部提示条原来**写死了"只有节点图有提示"**（那些键全是图操作）——
   * 于是页面编辑器里滚轮缩放、拖动吸附、双指缩放这些操作在界面上**无处可查**。
   *
   * ⚠️ 它就是**文案**，不是快捷键注册表：内核的 `shortcuts` 是给图那套
   * 命令用的（有 id / 可运行 / 可改键），而这里要的只是"告诉用户能怎么操作"。
   * 硬把不存在的命令塞进那张表，会让 `verify` 里"每个快捷键都真的能跑"
   * 那条断言变成假的。
   */
  tips?: readonly EditorTip[]
  /** 新建这个 Area 时的 Region 默认值（只写和基础默认不同的） */
  defaultRegions?: Partial<RegionState>
  /**
   * 编辑器主体。
   *
   * ⚠️ 内核的编辑器**没有**它 —— 那几个由宿主按 `area.editor` 分派
   * （节点图要画自研画布，属性窗口要和属性 Area 共用组件，都不是
   * 一个"外部组件"能表达的）。**包贡献的必须有它**，否则那个窗口
   * 开了是一片空白（注册表会当场抛错，见 `registerEditorContributions`）。
   */
  component?: unknown
  /** 贡献它的包 id（内核的编辑器没有这个字段）。只用于排错与提示文案 */
  from?: string
}

/**
 * **一个包贡献的编辑器**（`NodePackage.editors` 的元素）。
 *
 * ⚠️ 它必须是**代码包**才能给 —— `component` 是活的东西，JSON 里放不下。
 * 数据包（用户导入的 `.np-package.json`）带 `editors` 会被
 * `parsePackageFile` **明确拒绝**，而不是静默丢掉字段。
 *
 * 形状与 `EditorMeta` 的关系：贡献者只说自己知道的（叫什么、什么图标、
 * 支持哪些 Region、面板有哪些），由注册表补上 `implemented: true`
 * 与 `from: <包 id>`，并分配 `digit`。
 */
export interface EditorContribution {
  /**
   * 编辑器类型标识。
   *
   * ⚠️ 形状和节点类型一样是 `包.名字`：它会进布局文件、当 Map 的键、
   * 出现在 `data-editor` 上。**不许和内核已有的撞**（注册表抛错）——
   * 包占了 `graph` 就等于把内核的窗口换掉了，那种事不该"静默生效"。
   */
  type: string
  label: string
  icon: string
  /** 序号。不给就排在内核那几条之后自动分配；给了重复的会抛错 */
  digit?: number
  regions: readonly RegionKey[]
  panels?: Partial<Record<PanelSide, readonly PanelTabDef[]>>
  /**
   * **窗口头部的附加控件**（可选）。
   *
   * 和 `panels` 同一条理由：**窗口长什么样归包**。缩放档位 / 视图切换这类
   * "看哪儿"的操作属于窗口本身 —— 塞在正文工具栏里，它们会和
   * "往纸上加东西"混成一片（真机反馈："页面编辑器中有很多的按钮，
   * 这一部分可以放在窗口头部"）。
   *
   * ⚠️ 宿主**不认识**这个组件画的是什么，只负责摆位置（只传 `area`）：
   * 组件自己按 `area.id` 去读包的视图状态。所以这里**不许**出现
   * "按编辑器类型写死"的分支（那是 `node-packages.md` §4 的 B 路）。
   */
  header?: unknown
  /** 底部提示条上的操作提示（见 `EditorMeta.tips`） */
  tips?: readonly EditorTip[]
  defaultRegions?: Partial<RegionState>
  /**
   * 编辑器主体。
   *
   * ⚠️ 宿主只按 **props 进 / 事件出** 用它（见 `EditorHostProps`）：
   * 今天给它的是 `{ area, doc }` —— 窗口身份 + **文档读写口**，
   * 而不是宿主的内部状态。包不许去摸内核的单例
   * （见 `node-packages.md` §5「隔离」）。
   */
  component: unknown
}

/* ══════════════════════════════════════════════════════════════════
   ★★ 宿主 → 包编辑器 的**文档通道**（`EditorDocApi`）

   为什么需要它：`node-packages.md` §6.11 的「还没做」里写着
   「包要文档数据得等宿主开正式的 props」—— 而一个**页面编辑器**没有
   文档就什么都不是。D2（`print-template-architecture.md` §2.1）已经把话说死：

     节点图与画布**编辑同一份数据**；元素节点的几何是**参数**，不是连线。

   所以页面编辑器要的是「读节点参数 / 写节点参数」，而不是自己再存一份页面。

   ## 三条纪律（这三条决定了它长这样）

   ① **是通道，不是状态容器**。宿主仍然是文档的唯一持有者
      （`node-packages.md` §6.4 的倾向：props 进、emit 出）。
      这里给的是**只读快照 + 受控写入口**，没有"你可以随便改"的对象。

   ② **写入口只有那几个**，而且每一个都走宿主已有的那条写路径
      （历史 / 校验 / 引擎同步都在那里）。包**不能**拿到 `nodes` 数组直接改 ——
      绕过写入口的表现是「页面编辑器改的东西撤销不回去」，
      而那种 bug 只有用户会先发现。

   ③ **形状里一个打印概念都没有**。纸张 / mm / DPI / 条码全是包的
      （`package-boundaries.md` §10 的判据：换个域还要不要它）。
      这里只有「节点 / 连线 / 选中 / 求值结果 / 增删改」。
   ══════════════════════════════════════════════════════════════════ */

/**
 * **包编辑器看到的「一个节点」**（只读快照）。
 *
 * ⚠️ `values` 是**行 id → 控件值**，不是「某个包的参数表」：
 * 包自己定义节点类型，所以它当然知道自己那些行的 id
 * （`print.component.text` 的 `xMm` / `yMm`…）。
 * 内核不需要理解这些值 —— 它只是把它们递过去。
 */
export interface EditorNodeView {
  id: string
  /** 节点类型标识（`print.component.text`…）—— 包据此认自己的节点 */
  type: string
  title: string
  /**
   * 行 id → 控件的值（`property` / `input` 行）。
   *
   * ⚠️ **不含**变参行（那是端口，值是上游来的）；也不含「已连线的输入行」
   * 的真实值 —— 那种行的真值在 `outputs` 里（求值结果）。
   * 页面编辑器要显示「数据驱动的内容」时读 `outputs`，不要读这里。
   */
  values: Readonly<Record<string, unknown>>
  /** 图里的位置（版面编辑器不用；大纲 / 大缸那种视图会用） */
  position: { x: number; y: number }
  /** 提供它的包 id（缺包时是 `undefined` —— 节点照常在文档里） */
  pkg?: string
}

/** **包编辑器看到的「一条连线」**（只读快照） */
export interface EditorLinkView {
  id: string
  source: string
  sourceHandle: string | null
  target: string
  targetHandle: string | null
}

/**
 * **宿主交给包编辑器的文档读写口。**
 *
 * 一个对象，随 props 传进包贡献的编辑器**和它的侧栏面板**
 * （面板和主体是两个不相邻的插槽，见 `node-packages.md` §6.11 ——
 * 所以两边拿到的是**同一个** `doc`，不是各自一份）。
 *
 * ⚠️ 读写都是**同步**的：读的是当前这一层图的状态，写进去立刻生效
 * （宿主自己负责下一拍的重排 / 引擎同步）。异步 API 会让包作者
 * 在"等结果"里写出竞态，而那些竞态在真机上表现为"偶尔丢一次拖动"。
 */
export interface EditorDocApi {
  /** 当前这一层的节点（和节点图看的是同一层 —— 两个视图编辑同一份数据） */
  nodes: readonly EditorNodeView[]
  /** 当前这一层的连线 */
  links: readonly EditorLinkView[]
  /** 选中的节点 id（**和节点图共用**：一边选中，另一边高亮） */
  selection: readonly string[]
  /**
   * 求值结果：节点 id → 端口 id → 值。
   *
   * ⚠️ 只读，而且**可能缺**：节点缺包 / 循环引用 / 求值报错时那一条就不在。
   * 页面编辑器用它显示"这条数据实际是什么"（比如文本组件接了一个字段），
   * 而不是去猜。
   */
  outputs: ReadonlyMap<string, ReadonlyMap<string, unknown>>

  /* ── 写入口（全部走宿主的历史与校验）────────────────────────── */

  /**
   * **开始一次交互**（拖动 / 连续拖数值）—— 之后到 `endEdit` 之间的写
   * 合成**一步历史**。
   *
   * 为什么不是"每个写自己带一个合并窗口"：拖动是按帧写值的，一帧一次
   * 提交会塞进几十步历史（撤销一次只退回一帧，用户会以为撤销坏了）。
   * 而"多久算一次"这种事**只有编辑器知道**（它知道现在是拖拽还是打字），
   * 宿主不该替它猜。
   *
   * ⚠️ 必须和 `endEdit` 成对（`pointerup` / `pointercancel` 都要调）——
   * 忘了结束的表现是「后面所有改动都并进同一一步历史」。
   */
  beginEdit(label: string): void
  /** 结束一次交互（见 `beginEdit`） */
  endEdit(): void
  /**
   * 改一个节点的控件值。
   *
   * `coalesceMs > 0` = 「连续同类操作合并成一步历史」——
   * 面板里拖数字 / 连打输入用它（和节点图上拖滑块那 700ms 一个道理）。
   * 拖动这种"有明确开始结束"的交互用 `beginEdit` / `endEdit`，不要用它。
   */
  setValue(nodeId: string, rowId: string, value: unknown, coalesceMs?: number): void
  /**
   * 建一个节点。`at` 是**图坐标**（不是页面 mm —— 版面编辑器一般不给，
   * 让宿主按视口中心摆）；`values` 是新建时就带上的初值（行 id → 值），
   * 这样"在页面某个位置放一个文本"不用先建再改两次。
   *
   * @returns 新节点 id；类型不存在时 `null`（缺包）
   */
  addNode(
    type: string,
    at?: { x: number; y: number },
    values?: Readonly<Record<string, unknown>>,
  ): string | null
  /** 删掉一批节点（连同它们的连线） */
  removeNodes(ids: readonly string[]): void
  /**
   * **调整顺序**：把接到 `ownerId` 的 `rowId.N` 上的某个来源挪 `delta` 位（±1）。
   *
   * ⚠️ 层序 / 页序**都不是**一个 z-index 参数，而是变参行的**句柄序号**
   * （IR 是有序指令表：后画的压在上面；文档的 `pages.N` 决定第几页）。
   * 所以"挪一层"= 把两个来源的句柄序号换一下 —— 这个动作**只有宿主能做**：
   * 句柄不是行值，文档通道上没有"改句柄"的写入口。
   *
   * ⚠️ 签名是**通用的**（`ownerId` / `rowId` 都由包传）：宿主**不许**知道
   * `print.document` 这些包里的节点类型（`package-boundaries.md` §0 ——
   * 静态边界那条断言会红）。
   */
  reorderChildren(ownerId: string, rowId: string, childId: string, delta: number): void
  /**
   * 连一条线（会过宿主的类型 / 结构校验）。
   *
   * @returns 接上了没有 —— 类型对不上时 `false`，包应当据此提示用户
   */
  connect(source: string, sourceHandle: string, target: string, targetHandle: string): boolean
  /** 选中一批节点（同步到节点图那一边的高亮） */
  select(ids: readonly string[]): void
}

/**
 * **宿主交给包贡献的编辑器（和它的侧栏面板）的全部 props。**
 *
 * ⚠️ 这里就是「包能拿到什么」的**清单** —— 加字段之前先问：
 * 这是不是又一个"因为打印需要"才加的东西（`package-boundaries.md` §10）？
 */
/**
 * 包自己算出来的**一条诊断**（进不了"求值 / 校验"那两条流水线的东西）。
 *
 * ⚠️ 名字特意**不叫** `EditorProblem`：那个名字已经被注册表占了，
 * 指的是"这个包的编辑器贡献有问题"（形状也不同）。两者混起来会很难查。
 *
 * ⚠️ 为什么不让包自己去画一个列表：真机反馈要的是"**统一**在输出窗口里看"
 * —— 一个窗口一个列表的话，用户得记住"哪个问题在哪个窗口的哪一角"。
 */
export interface EditorDiagnostic {
  severity: 'error' | 'warning' | 'info'
  message: string
  /** 相关节点（有的话，节点上的徽标也会亮） */
  nodeId?: string
}

export interface EditorHostProps {
  /** 窗口身份（同一个编辑器组件可能同时开在两个窗口里） */
  area: { id: string }
  /** 文档读写口（见 `EditorDocApi`） */
  doc: EditorDocApi
  /**
   * **把这一批问题交给宿主**（整批替换，不是追加）。
   *
   * ⚠️ 传**整批**而不是"加一条"：问题列表是**派生**的（改好了就该消失），
   * 追加式会让历史里堆满早就修好的旧快照 —— 输出窗口的既有实现
   * （`useProblems`）也是这个取向。
   *
   * ⚠️ 不传这个字段时包**照样工作**（编辑器里仍有它自己的详情浮层）：
   * 它是"多一条出口"，不是"必须实现的接口"。
   */
  publishDiagnostics?: (senderId: string, problems: readonly EditorDiagnostic[]) => void
  /**
   * **打开输出窗口**（把焦点带到那块窗口上）。
   *
   * ⚠️ 这是"窗口操作"，所以归宿主：包不知道布局里有几块窗口、
   * 也不知道输出窗口这一版叫什么。包能做的只是"用户点了感叹号，
   * 我想让他看到详情"。
   */
  openOutput?: () => void
}

/**
 * 一个包里那批编辑器贡献的来源（`NodePackage` 的最小投影）。
 *
 * ⚠️ 注册表只要这两样，**不要整个 `NodePackage`** —— 那会让
 * `layout/` 反向依赖 `node/`，而依赖方向是单向的（`verify-layers`）。
 */
export interface EditorContributionSource {
  /** 包 id（写进 `EditorMeta.from`，也让报错能点名是谁） */
  id: string
  editors?: readonly EditorContribution[]
}
